/**
 * Espelho TypeScript de `tokens.css`.
 *
 * Mesmo arquivo/nome que existe no SGA Petro Frontend
 * (`src/styles/customTokens.ts`) — ao integrar, apague este e importe o de lá.
 */
import type { ThemeConfig } from 'antd';

export const cores = {
  primary: '#01a863',
  primaryTab: '#009859',
  primaryText: '#009859',
  primarySubtext: '#818181',
  primarySelecao: '#dbfadd9c',

  success: '#30db4a',
  warning: '#e69138',
  error: '#ff4d4f',
  tagBg: 'rgba(22, 162, 73, 0.1)',
  tagText: '#16a249',

  orange1: '#ffa500',
  blue3: '#008ae6',

  bgPage: '#f0f0f0',
  bgForm: '#ebefee',
  bgModal: 'rgb(235, 239, 238)',
  bgContainer: '#ffffff',

  gray05: '#f7f7f7',
  gray2: '#eeebeb',
  gray4: '#cccccc',
  gray5: '#a8a8a8',

  border: '#cecccc',
  borderGray: '#dde4e2',

  fg1: 'rgba(0, 0, 0, 0.88)',
  fg2: 'rgba(0, 0, 0, 0.65)',
  fg3: '#818181',
} as const;

export const coresDark = {
  ...cores,
  primary: '#017545',
  primaryTab: '#016039',
  primaryText: '#02b168',
  primarySelecao: '#0b3011',

  bgPage: '#2f2f2f',
  bgForm: '#2f2f2f',
  bgModal: 'rgb(47, 47, 47)',
  bgContainer: '#1b1a1a',

  gray05: '#1b1a1a',
  gray2: '#1b1a1a',
  gray4: '#595959',
  gray5: '#818181',

  border: '#434643',
  borderGray: '#141414',

  fg1: 'rgba(255, 255, 255, 0.88)',
  fg2: 'rgba(255, 255, 255, 0.65)',
  fg3: '#7f8080',
} as const;

export const raios = {
  card: 15,
  form: 16,
  md: 10,
  sm: 6,
  xs: 4,
  /** Canônico do produto: inputs, botões, tags e segmented são pílula. */
  pill: 999,
} as const;

export const fontes = {
  familia: '"Roboto", -apple-system, BlinkMacSystemFont, "Segoe UI", Arial, sans-serif',
  mono: 'ui-monospace, "SFMono-Regular", "Menlo", "Consolas", monospace',
  base: 14,
  sm: 13,
  xs: 12,
} as const;

/** Tema do Ant Design derivado dos tokens. */
export function temaAntd(modo: 'light' | 'dark' = 'light'): ThemeConfig {
  const c = modo === 'dark' ? coresDark : cores;

  return {
    token: {
      colorPrimary: c.primary,
      colorSuccess: c.tagText,
      colorWarning: c.warning,
      colorError: c.error,
      colorText: c.fg1,
      colorTextSecondary: c.fg2,
      colorTextTertiary: c.fg3,
      colorBgLayout: c.bgPage,
      colorBgContainer: c.bgContainer,
      colorBorder: c.border,
      colorBorderSecondary: c.borderGray,
      fontFamily: fontes.familia,
      fontFamilyCode: fontes.mono,
      fontSize: fontes.base,
      borderRadius: raios.md,
      // O produto é denso: 32px de controle, não os 40 do default do antd.
      controlHeight: 32,
    },
    components: {
      Button: { borderRadius: raios.pill, controlHeight: 32 },
      Input: { borderRadius: raios.pill },
      Select: { borderRadius: raios.pill },
      Tag: { borderRadiusSM: raios.pill },
      Segmented: { borderRadius: raios.pill, itemSelectedBg: c.primary, itemSelectedColor: '#fff' },
      Card: { borderRadiusLG: raios.card },
      Modal: { borderRadiusLG: raios.form },
      Table: { headerBg: c.gray05, rowHoverBg: c.primarySelecao, cellPaddingBlockSM: 3 },
    },
  };
}
